// services/UserService.js - Phone-based authentication
import { BaseService, resetRefreshAttempts } from './BaseService';
import { Token } from '../models';
import { Preferences } from '@capacitor/preferences';
import { isCapacitor, isElectron } from '../utils/platform';
import { Device } from '@capacitor/device';
import API_CONFIG from '../config/api';
import { tokenManager } from './TokenManager';
import { secureTokenStorage } from './SecureTokenStorage';
import { tokenLog, tokenWarn, tokenError } from './Debug';

// Lazy load profileManager to avoid circular dependency
let profileManager;
const getProfileManager = () => {
  if (!profileManager) {
    // Import on first use to avoid circular dependency at module load time
    const { profileManager: pm } = require('./index.js');
    profileManager = pm;
  }
  return profileManager;
};

export class UserService extends BaseService {
  constructor() {
    super(API_CONFIG.BASE_URL);
    // Set userService reference in profileManager after a short delay to avoid circular dependency
    setTimeout(() => {
      const pm = getProfileManager();
      if (pm) {
        pm.userService = this;
      }
    }, 0);
  }

  // ===== TOKEN MANAGEMENT =====

  // Token validation
  isTokenValid(token) {
    if (!token) {
      return false;
    }

    try {
      // Check if token has the correct JWT format (3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length !== 3) {
        tokenError('Invalid token format - expected 3 parts, got:', parts.length);
        return false;
      }

      // Decode the payload (middle part)
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;

      // Check if token is expired (add 5 minute buffer)
      const isValid = payload.exp > (currentTime + 300);

      return isValid;
    } catch (error) {
      tokenError('isTokenValid() error', error);
      return false;
    }
  }

  // Check if token expires soon
  isTokenExpiringSoon(token, bufferMinutes = 5) {
    if (!token) {
      return true; // If no token, consider it as expiring
    }

    try {
      // Check if token has the correct JWT format
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true; // Invalid format, consider as expiring
      }

      // Decode the payload
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      const bufferSeconds = bufferMinutes * 60;

      // Check if token expires within the buffer time
      const expiresIn = payload.exp - currentTime;

      return expiresIn <= bufferSeconds;
    } catch (error) {
      tokenError('Error checking token expiration:', error);
      return true; // On error, consider as expiring to be safe
    }
  }

  // Authentication check
  async isAuthenticated() {
    const token = await this.getAccessToken();
    return token && this.isTokenValid(token);
  }

  async getAccessToken() {
    if (isCapacitor) {
      const { value } = await Preferences.get({ key: 'access_token' });
      // Sync with TokenManager if we have a stored token
      if (value && !tokenManager.getToken()) {
        // Try to get expiration from storage
        const { value: expStr } = await Preferences.get({ key: 'access_token_exp' });
        const exp = expStr ? parseInt(expStr) : Date.now() + 3200000; // Default 30 min
        const remainingSec = Math.max(0, Math.floor((exp - Date.now()) / 1000));
        await tokenManager.setToken(value, remainingSec);
        tokenLog('getAccessToken(): synced token from Preferences into TokenManager', { exp });
      }
      return value || tokenManager.getToken();
    }
    return tokenManager.getToken();
  }

  async getRefreshToken() {
    if (isCapacitor) {
      const { value } = await Preferences.get({ key: 'refresh_token' });
      return value;
    }
    if (isElectron) {
      // For Electron, use localStorage like mobile
      return localStorage.getItem('refreshToken');
    }
    // For web, refresh token is in httpOnly cookie - no need to get it manually
    return null;
  }

  async setTokens(accessToken, refreshToken, expiresIn = null) {
    // Let TokenManager decode the JWT and use server's expiration time
    await tokenManager.setToken(accessToken, null); // Let TokenManager decode JWT
    tokenLog('setTokens(): access token set in TokenManager');

    if (isCapacitor) {
      // Store in Capacitor Preferences for persistence
      await Preferences.set({ key: 'access_token', value: accessToken });

      if (refreshToken) {
        await Preferences.set({ key: 'refresh_token', value: refreshToken });
        // Also store in secure storage
        if (accessToken && refreshToken) {
          await secureTokenStorage.storeSecureTokens(accessToken, refreshToken, tokenManager.expiration);
        }
        tokenLog('setTokens(): stored refresh token and secure backup (Capacitor)', { hasRefresh: !!refreshToken, exp: tokenManager.expiration });
      }
    } else if (isElectron) {
      // For Electron, store refresh token in localStorage like mobile
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
        tokenLog('setTokens(): stored refreshToken (Electron)');
      }
    }
    // For web only, refresh token is set as httpOnly cookie by the server
  }

  async clearTokens() {
    // Clear from TokenManager first
    await tokenManager.clear();

    if (isCapacitor) {
      await Preferences.remove({ key: 'access_token' });
      await Preferences.remove({ key: 'access_token_exp' });
      await Preferences.remove({ key: 'refresh_token' });
      await Preferences.remove({ key: 'user_data' });
    }

    localStorage.removeItem('refreshToken');
  }

  // ===== DEVICE FUNCTIONS =====

  async getDeviceId() {
    if (isCapacitor) {
      try {
        const info = await Device.getId();
        return info.uuid || info.identifier || 'unknown-mobile-device';
      } catch (error) {
        tokenError('Error getting device ID:', error);
        return 'unknown-mobile-device';
      }
    }

    // For web/desktop, generate or retrieve a device ID
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = 'web-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  async getDeviceName() {
    if (isCapacitor) {
      try {
        const info = await Device.getInfo();
        return info.name || info.model || 'Mobile Device';
      } catch (error) {
        tokenError('Error getting device name:', error);
        return 'Mobile Device';
      }
    }

    // For web/desktop
    return navigator.userAgent.includes('Electron') ? 'Desktop App' : 'Web Browser';
  }

  async getDeviceInfo() {
    return {
      id: await this.getDeviceId(),
      name: await this.getDeviceName(),
      platform: isCapacitor ? 'mobile' : (isElectron ? 'mobile' : 'web') // Electron = mobile for auth
    };
  }

  // ===== PHONE-BASED AUTHENTICATION =====

  // Start phone authentication (sends OTP)
  async startPhoneAuth(phone, channel = 'sms') {
    const response = await this.post('/auth/phone/start', {
      phone,
      channel
    }, { skipAuth: true });

    tokenLog('startPhoneAuth(): response', response);
    return response;
  }

  // Start phone authentication for authenticated users (protected route - no phone needed)
  async startPhoneAuthProtected(channel = 'sms') {
    const response = await this.post('/auth/phone/start/protected', {
      channel
    });

    tokenLog('startPhoneAuthProtected(): response', response);
    return response;
  }

  // Verify phone with OTP code
  async verifyPhoneCode(phone, code) {
    const response = await this.post('/auth/phone/verify', {
      phone,
      code
    }, { skipAuth: true });

    tokenLog('verifyPhoneCode(): response', response);
    return response;
  }

  // ===== EMAIL-BASED LOGIN =====

  // Start email login (sends code)
  async startEmailLogin(email) {
    const response = await this.post('/auth/email-login/start', {
      email
    }, { skipAuth: true });

    tokenLog('startEmailLogin(): response', response);
    return response;
  }

  // Complete email login with code and optional PIN
  async completeEmailLogin(email, code, pin = null, deviceInfo = null) {
    if (!deviceInfo) {
      deviceInfo = await this.getDeviceInfo();
    }

    try {
      const response = await this.post('/auth/email-login/complete', {
        email,
        code,
        pin,
        deviceInfo
      }, { skipAuth: true });

      const responseData = response.data || response;

      const { accessToken, refreshToken, expiresIn, user } = responseData;

      if (!accessToken) {
        throw new Error('No access token in email login response');
      }

      await this.setTokens(accessToken, refreshToken, expiresIn);

      const profile = user ? getProfileManager().loadFromAPI(user) : null;

      resetRefreshAttempts();

      const hasProfile = responseData?.hasProfile;

      return {
        profile,
        token: new Token({ accessToken, refreshToken, expiresIn }),
        user,
        hasProfile
      };
    } catch (error) {
      tokenError('completeEmailLogin(): error details', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  // Set PIN for new user
  async setPin(phone, pin) {
    const response = await this.post('/auth/pin/set', {
      phone,
      pin
    }, { skipAuth: true });

    tokenLog('setPin(): response', response);

    // Handle both response.data and direct response formats
    const responseData = response?.data || response;

    const { accessToken, refreshToken, expiresIn, user } = responseData || {};

    if (!accessToken) {
      // If backend did not return tokens, keep backward compatibility
      return response;
    }

    // Store tokens
    await this.setTokens(accessToken, refreshToken, expiresIn);

    // Load profile into ProfileManager when available
    const profile = user ? getProfileManager().loadFromAPI(user) : null;

    // Reset refresh attempts on successful auth
    resetRefreshAttempts();

    // Surface hasProfile flag if backend provided it (top-level or within user)
    const hasProfile = responseData?.hasProfile;

    return {
      profile,
      token: new Token({ accessToken, refreshToken, expiresIn }),
      user,
      hasProfile
    };
  }

  // Set email (optional)
  async setEmail(phone, email) {
    const response = await this.post('/auth/email/set', {
      phone,
      email
    }, { skipAuth: true });

    tokenLog('setEmail(): response', response);
    return response;
  }

  // Unified registration: PIN + optional email + profile in one call
  async registerUnified(phone, pin, firstName, lastName, email = null, title = null, specialty = null, deviceInfo = null) {
    if (!deviceInfo) {
      deviceInfo = await this.getDeviceInfo();
    }

    try {
      const response = await this.post('/auth/register/unified', {
        phone,
        pin,
        email: email || undefined,
        firstName,
        lastName,
        title: title || undefined,
        specialty: specialty || undefined,
        deviceInfo
      }, { skipAuth: true });

      const responseData = response?.data || response;
      const { accessToken, refreshToken, expiresIn, user } = responseData;

      if (!accessToken) {
        throw new Error('No access token in unified registration response');
      }

      await this.setTokens(accessToken, refreshToken, expiresIn);

      const profile = user ? getProfileManager().loadFromAPI(user) : null;

      resetRefreshAttempts();

      return {
        profile,
        token: new Token({ accessToken, refreshToken, expiresIn }),
        user,
        hasProfile: true,
        emailVerificationSent: responseData.emailVerificationSent
      };
    } catch (error) {
      tokenError('registerUnified(): error', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  // Complete login with OTP and PIN
  async completeLogin(phone, code, pin, deviceInfo = null) {
    // Get device info if not provided
    if (!deviceInfo) {
      deviceInfo = await this.getDeviceInfo();
    }

    try {
      const response = await this.post('/auth/login/complete', {
        phone,
        code,
        pin,
        deviceInfo
      }, { skipAuth: true });

      // Log the response structure for debugging
      tokenLog('completeLogin(): response structure', {
        hasData: !!response.data,
        hasDirectTokens: !!(response.accessToken || response.refreshToken),
        responseKeys: Object.keys(response || {}),
        dataKeys: response.data ? Object.keys(response.data) : []
      });

      // Handle both response.data and direct response formats
      const responseData = response.data || response;

      // Extract tokens and profile data from response
      const { accessToken, refreshToken, expiresIn, user } = responseData;

      if (!accessToken) {
        throw new Error('No access token in login response');
      }

      // Store tokens with expiration
      await this.setTokens(accessToken, refreshToken, expiresIn);

      // Load profile data into ProfileManager
      const profile = user ? getProfileManager().loadFromAPI(user) : null;

      // Reset refresh attempts on successful login
      resetRefreshAttempts();

      // Surface hasProfile flag if backend provided it (top-level or within user)
      const hasProfile = responseData?.hasProfile;

      return {
        profile,
        token: new Token({ accessToken, refreshToken, expiresIn }),
        user,
        hasProfile
      };
    } catch (error) {
      tokenError('completeLogin(): error details', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  // Test login endpoint for test account (phone: 000 000 00 00)
  async testLogin(phone, code, pin, deviceInfo = null) {
    // Get device info if not provided
    if (!deviceInfo) {
      deviceInfo = await this.getDeviceInfo();
    }

    try {
      const response = await this.post('/auth/login/test', {
        phone,
        code,
        pin,
        deviceInfo
      }, { skipAuth: true });

      // Log the response structure for debugging
      tokenLog('testLogin(): response structure', {
        hasData: !!response.data,
        hasDirectTokens: !!(response.accessToken || response.refreshToken),
        responseKeys: Object.keys(response || {}),
        dataKeys: response.data ? Object.keys(response.data) : []
      });

      // Handle both response.data and direct response formats
      const responseData = response.data || response;

      // Extract tokens and profile data from response
      const { accessToken, refreshToken, expiresIn, user } = responseData;

      if (!accessToken) {
        throw new Error('No access token in test login response');
      }

      // Store tokens with expiration
      await this.setTokens(accessToken, refreshToken, expiresIn);

      // Load profile data into ProfileManager
      const profile = user ? getProfileManager().loadFromAPI(user) : null;

      // Reset refresh attempts on successful login
      resetRefreshAttempts();

      // Surface hasProfile flag if backend provided it (top-level or within user)
      const hasProfile = responseData?.hasProfile;

      return {
        profile,
        token: new Token({ accessToken, refreshToken, expiresIn }),
        user,
        hasProfile
      };
    } catch (error) {
      tokenError('testLogin(): error details', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }

  // Request PIN recovery via email
  async requestRecovery(email) {
    const response = await this.post('/auth/recover/request', {
      email
    }, { skipAuth: true });

    tokenLog('requestRecovery(): response', response);
    return response;
  }

  // Complete PIN recovery with token
  async completeRecovery(token, newPin) {
    const response = await this.post('/auth/recover/complete', {
      token,
      newPin
    }, { skipAuth: true });

    tokenLog('completeRecovery(): response', response);
    return response;
  }

  // Complete profile (after initial registration)
  async completeProfile(firstName, lastName, title = null, specialty = null, deviceInfo = null) {
    // Get device info if not provided
    if (!deviceInfo) {
      deviceInfo = await this.getDeviceInfo();
    }

    const response = await this.post('/auth/register/complete', {
      firstName,
      lastName,
      title,
      specialty,
      deviceInfo
    });

    tokenLog('completeProfile(): response', response);
    const profile = getProfileManager().loadFromAPI(response?.data || response);

    return {
      success: true,
      data: profile
    };
  }

  // ===== TRUSTED DEVICE AUTHENTICATION (Mobile) =====

  // Get challenge for device authentication
  async getDeviceChallenge(deviceId) {
    const response = await this.post('/auth/device/challenge', {
      deviceId
    }, { skipAuth: true });

    // Response is already the parsed data
    return response;
  }

  // Verify device with signature
  async verifyDevice(deviceId, signature, pin = null, deviceInfo = null) {
    // Get device info if not provided
    if (!deviceInfo) {
      deviceInfo = await this.getDeviceInfo();
    }

    const response = await this.post('/auth/device/verify', {
      deviceId,
      signature,
      pin,
      deviceInfo
    }, { skipAuth: true });

    // Extract tokens and profile data from response (response is already parsed)
    const { accessToken, refreshToken, expiresIn, user } = response;

    // Store tokens with expiration
    await this.setTokens(accessToken, refreshToken, expiresIn);

    // Load profile data into ProfileManager
    const profile = getProfileManager().loadFromAPI(user);

    // Reset refresh attempts on successful login
    resetRefreshAttempts();

    return {
      profile,
      token: new Token({ accessToken, refreshToken, expiresIn }),
      user
    };
  }

  // Trust/enroll device (mobile only)
  async trustDevice(deviceId, deviceName, platform, publicKey = null) {
    const response = await this.post('/users/devices/trust', {
      deviceId,
      deviceName,
      platform,
      publicKey
    });

    return response.data || response;
  }

  // Get list of trusted devices
  async getTrustedDevices() {
    const response = await this.get('/users/devices');
    return response.data;
  }

  // Remove a trusted device
  async removeTrustedDevice(deviceId) {
    const response = await this.delete(`/users/devices/${deviceId}`);
    return response.data;
  }

  // ===== TOKEN REFRESH =====

  async refreshToken() {
    try {
      // For web: Use credentials: 'include' to send httpOnly cookies
      // For mobile/electron: Still need to send refresh token in body
      let requestOptions = {
        platform: isCapacitor ? 'mobile' : (isElectron ? 'mobile' : 'web')
      };

      if (isCapacitor || isElectron) {
        const refreshToken = await this.getRefreshToken();

        if (!refreshToken) {
          tokenError('refreshToken(): no refresh token available on mobile/electron');
          throw new Error('No refresh token available - re-authentication required');
        }
        requestOptions.refreshToken = refreshToken;
      }

      const response = await this.request('/auth/refresh', {
        method: 'POST',
        credentials: 'include', // This sends httpOnly cookies
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestOptions),
        skipAuth: true
      });

      // Validate response structure
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid refresh token response');
      }

      // Handle both response.data and direct response formats
      const tokenData = response.data || response;

      if (!tokenData.accessToken) {
        throw new Error('No access token in refresh response');
      }

      // Store the new tokens with expiration
      await this.setTokens(
        tokenData.accessToken,
        tokenData.refreshToken,
        tokenData.expiresIn
      );
      tokenLog('refreshToken(): tokens updated after refresh');

      return new Token(tokenData);
    } catch (error) {
      tokenError('refreshToken(): error', error);
      throw error;
    }
  }

  // ===== LOGOUT =====

  async logout() {
    try {
      // For web: Use credentials: 'include' to send httpOnly cookies
      // For mobile/electron: Send refresh token in body
      let requestOptions = {
        platform: isCapacitor ? 'mobile' : (isElectron ? 'mobile' : 'web')
      };

      if (isCapacitor || isElectron) {
        const refreshToken = await this.getRefreshToken();
        if (refreshToken) {
          requestOptions.refreshToken = refreshToken;
        }
      }

      await this.request('/auth/logout', {
        method: 'POST',
        credentials: 'include', // This sends httpOnly cookies
        headers: { 'Content-Type': 'application/json' },
        body: Object.keys(requestOptions).length > 0 ? JSON.stringify(requestOptions) : undefined
      });
    } catch (error) {
      tokenError('logout(): request failed', error);
      // Continue with local cleanup even if server request fails
    } finally {
      // Clear sensitive data
      getProfileManager().clear();
      await this.clearTokens();
      try {
        await secureTokenStorage.clearSecureTokens();
      } catch (e) {
        tokenError('Error clearing secure tokens on logout:', e);
      }

      // Selectively clear storage instead of wiping everything
      if (isCapacitor) {
        try {
          const keysToPreserve = [
            'biometric_enabled',
            'device_trusted',
            'device_fingerprint',
            'device_private_key',
            'device_public_key_pem',
            'device_private_key_stored',
            'device_name',
            'device_trusted_date',
            'deviceId'
          ];

          const { keys } = await Preferences.keys();

          for (const key of keys) {
            if (!keysToPreserve.includes(key)) {
              await Preferences.remove({ key });
            }
          }
        } catch (error) {
          tokenError('logout(): error during selective clear of Capacitor storage', error);
        }
      } else {
        // For web, preserve deviceId
        const deviceId = localStorage.getItem('deviceId');
        localStorage.clear();
        if (deviceId) {
          localStorage.setItem('deviceId', deviceId);
        }
      }
    }
  }

  async logoutAll() {
    const response = await this.post('/users/logout-all');

    // Clear everything
    getProfileManager().clear();
    await this.clearTokens();
    localStorage.clear();

    if (isCapacitor) {
      try {
        await Preferences.clear();
      } catch (error) {
        tokenError('Error clearing Capacitor storage:', error);
      }
    }

    return response.data;
  }

  // ===== PROFILE MANAGEMENT =====

  async getProfile() {
    const response = await this.get('/users/profile');
    return response.data;
  }

  async updateProfile(updates) {
    const response = await this.put('/users/profile', updates);

    // If we get complete profile data back, reload it
    if (response.data?.user?.email && response.data?.expiresAt) {
      getProfileManager().loadFromAPI(response.data);
      return getProfileManager().getCurrentProfile();
    }

    // Otherwise, update partially
    getProfileManager().updateProfile(updates);
    return getProfileManager().getCurrentProfile();
  }

  async updateDeviceToken(deviceToken, platform) {
    await this.post('/users/device-token', { deviceToken: deviceToken, platform: platform });
  }

  // ===== ACCOUNT MANAGEMENT =====

  /**
   * Delete user account with phone verification
   * @param {string} phone - Phone number in E.164 format
   * @param {string} code - OTP code from SMS
   * @param {string} pin - User's 6-digit PIN
   */
  async deleteAccount(phone, code, pin) {
    await this.post('/users/account/delete', { phone, code, pin });

    // Clear everything
    getProfileManager().clear();
    await this.clearTokens();
  }

  // ===== SESSION MANAGEMENT =====

  async getSessions() {
    const response = await this.get('/users/sessions');
    return response.data;
  }

  async revokeSession(sessionId) {
    await this.delete(`/users/sessions/${sessionId}`);
  }

  // ===== EMAIL MANAGEMENT =====

  /**
   * Change primary email with phone verification
   * @param {string} phone - Phone number in E.164 format
   * @param {string} code - OTP code from SMS/WhatsApp
   * @param {string} pin - User's 6-digit PIN
   * @param {string} newEmail - New email address
   * @returns {Promise} Response with success message
   */
  async changeEmail(phone, code, pin, newEmail) {
    const response = await this.post('users/email/change', {
      phone,
      code,
      pin,
      newEmail
    });
    return response.data;
  }

  /**
   * Send verification code to the new email address to initiate email change
   * @param {string} newEmail - New email address
   * @returns {Promise} Response data
   */
  async sendEmailChangeCode(newEmail) {
    const response = await this.post('/users/email/change/send-code', { newEmail });
    return response.data || response;
  }

  /**
   * Complete email change with email code, phone SMS code, and PIN
   * @param {string} newEmail - New email address
   * @param {string} emailCode - Code sent to the new email (6 digits)
   * @param {string} code - Phone SMS code
   * @param {string} pin - User's 6-digit PIN
   * @returns {Promise} Response data
   */
  async completeEmailChange(newEmail, emailCode, code, pin) {
    const response = await this.post('/users/email/change', {
      newEmail,
      emailCode,
      code,
      pin
    });
    return response.data || response;
  }

  // ===== PHONE NUMBER MANAGEMENT =====

  /**
   * Send email verification code to start phone number change
   * @returns {Promise} Response with success message
   */
  async sendPhoneChangeEmailCode() {
    const response = await this.post('/users/phone/change/send-email-code');
    return response.data;
  }

  /**
   * Change phone number with email and SMS verification
   * @param {string} emailCode - Code received via email
   * @param {string} newPhone - New phone number in E.164 format
   * @param {string} newPhoneCode - OTP code sent to new phone
   * @param {string} pin - User's 6-digit PIN
   * @returns {Promise} Response with success message
   */
  async changePhone(emailCode, newPhone, newPhoneCode, pin) {
    const response = await this.post('/users/phone/change', {
      emailCode,
      newPhone,
      newPhoneCode,
      pin
    });
    return response.data;
  }

  // ===== USER LOGO MANAGEMENT =====

  async getUserLogo() {
    return this.getBlob('/users/logo');
  }

  async uploadUserLogo(logoFile) {
    if (!logoFile) throw new Error('logo file is required');
    const formData = new FormData();
    formData.append('logo', logoFile);
    return this.postFormData('/users/logo', formData);
  }

  async deleteUserLogo() {
    return this.delete('/users/logo');
  }
}